import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Users, Briefcase, FileText, Clock, Wallet, BadgeDollarSign,
  BarChart3, Bell, ShieldCheck, Settings, LogOut, Building2, Search, TrendingUp,
  LineChart, Upload, PiggyBank, Archive, AlertTriangle, UserRound,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/Avatar";
import NotificationBanner from "@/components/NotificationBanner";
import RecentActivityBanner from "@/components/RecentActivityBanner";
import { toast } from "sonner";
import useIdleTimeout from "@/hooks/useIdleTimeout";

// 30 minutes of no user input → silent sign-out
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "business_development", "accounts"] },
  { to: "/clients", label: "Clients", icon: Building2, roles: ["admin", "business_development", "accounts"] },
  { to: "/projects", label: "Projects", icon: Briefcase, roles: ["admin", "business_development", "accounts"] },
  { to: "/projects-closed", label: "Closed Projects", icon: Archive, roles: ["admin", "business_development", "accounts"] },
  { to: "/invoices", label: "Invoices", icon: FileText, roles: ["admin", "business_development", "accounts"] },
  { to: "/pending-invoices", label: "Pending Invoices", icon: Clock, roles: ["admin", "business_development", "accounts"] },
  { to: "/pending-payments", label: "Pending Payments", icon: Wallet, roles: ["admin", "business_development", "accounts"] },
  { to: "/payments", label: "Payments Received", icon: BadgeDollarSign, roles: ["admin", "business_development", "accounts"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "business_development", "accounts"] },
  { to: "/revenue", label: "Revenue", icon: PiggyBank, roles: ["admin", "accounts"] },
  { to: "/analytics", label: "Analytics", icon: LineChart, roles: ["admin", "business_development", "accounts"] },
  { to: "/bd-outstanding", label: "BD Outstanding", icon: Wallet, roles: ["admin", "business_development"] },
  { to: "/incentives", label: "BD Incentives", icon: TrendingUp, roles: ["admin", "business_development"] },
  { to: "/import", label: "Data Import", icon: Upload, roles: ["admin", "business_development", "accounts"] },
  { to: "/notifications", label: "Notifications", icon: Bell, roles: ["admin", "business_development", "accounts"] },
  { to: "/users", label: "Users", icon: Users, roles: ["admin"] },
  { to: "/team", label: "Team", icon: UserRound, roles: ["admin", "business_development", "accounts"] },
  { to: "/audit-log", label: "Audit Log", icon: ShieldCheck, roles: ["admin"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["admin", "business_development", "accounts"] },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = React.useState("");

  // Silent sign-out after 30 min of idle. Disabled while auth is still loading
  // or the user hasn't authenticated.
  useIdleTimeout({
    timeoutMs: IDLE_TIMEOUT_MS,
    disabled: !user || user === false,
    onIdle: async () => {
      try { await logout(); } finally {
        toast.message("Signed out for security", { description: "You were inactive for 30 minutes." });
        navigate("/login");
      }
    },
  });

  const roleLabel = {
    admin: "Administrator",
    business_development: "Business Development",
    accounts: "Accounts",
  }[user?.role] || user?.role;

  const showSeedNudge = user?.role === "admin" && user?.is_seed_password === true;

  const visibleNav = React.useMemo(
    () => nav.filter((n) => hasRole(user, ...n.roles)),
    [user]
  );

  return (
    <div className="h-screen flex bg-slate-50 text-slate-900 overflow-hidden">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 sticky top-0 h-screen bg-white border-r border-slate-200 flex flex-col">
        <div className="h-16 flex items-center px-4 border-b border-slate-200">
          <img
            src="/tecticonism-wordmark-sm.png"
            alt="Tecticonism Studio"
            className="h-6 w-auto object-contain"
            data-testid="sidebar-logo"
          />
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.to.replace("/", "") || "dashboard"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-fast border-l-2 ${
                  isActive
                    ? "bg-brand-tint text-brand-dark border-brand font-medium"
                    : "text-slate-600 border-transparent hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-2 px-2 py-2">
            <Avatar src={user?.avatar_url} name={user?.name} size="w-8 h-8" data-testid="sidebar-avatar" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{user?.name}</div>
              <div className="text-[10px] text-slate-500 truncate">{roleLabel}</div>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              data-testid="logout-btn"
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {user && <NotificationBanner />}
        {user && <RecentActivityBanner />}
        <header className="h-16 sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center px-6 gap-4">
          <div className="flex-1 flex items-center gap-2 max-w-xl">
            <Search className="w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search clients, projects, invoices…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") navigate(`/projects?q=${encodeURIComponent(query)}`);
              }}
              data-testid="global-search"
              className="h-9 border-slate-200"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/notifications")}
            data-testid="header-notifications"
          >
            <Bell className="w-4 h-4 mr-2" /> Notifications
          </Button>
          {showSeedNudge && (
            <button
              type="button"
              onClick={() => navigate("/settings?action=change-password")}
              data-testid="seed-password-nudge"
              className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 hover:bg-amber-100 transition-colors"
              title="Your account is still using the seed password. Click to set a permanent one."
            >
              <AlertTriangle className="w-3 h-3" />
              Change seed password
            </button>
          )}
        </header>
        <main className="flex-1 p-6 md:p-8 overflow-auto min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
